import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, User } from 'lucide-react';
import { NotificationBell } from '../notifications/NotificationBell';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_LOGO = "/logo_event.png"; // frontend/public/logo_event.png
const resolveLogo = (logoUrl) => {
  if (!logoUrl) return DEFAULT_LOGO;
  if (logoUrl.startsWith('/')) return (BACKEND_URL ? `${BACKEND_URL}${logoUrl}` : logoUrl);
  return logoUrl;
};

// Helper to get initial member from localStorage
const getInitialMember = () => {
  try {
    const token = localStorage.getItem('memberToken');
    const memberData = localStorage.getItem('memberData');
    if (token && memberData) {
      return JSON.parse(memberData);
    }
  } catch {
    return null;
  }
  return null;
};

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isEventsMenuOpen, setIsEventsMenuOpen] = useState(false);
  const [isInfoMenuOpen, setIsInfoMenuOpen] = useState(false);
  const [member, setMember] = useState(getInitialMember);
  const [siteConfig, setSiteConfig] = useState(null);
  const [customNavItems, setCustomNavItems] = useState([]);
  const location = useLocation();
  const eventsMenuRef = useRef(null);
  const infoMenuRef = useRef(null);

  useEffect(() => {
    const fetchSiteConfig = async () => {
      try {
        const response = await axios.get(`${API}/site-config`);
        setSiteConfig(response.data);
      } catch (error) {
        console.error('Error fetching site config:', error);
      }
    };
    const fetchNavigation = async () => {
      try {
        const response = await axios.get(`${API}/navigation`);
        setCustomNavItems(response.data || []);
      } catch {}
    };
    fetchSiteConfig();
    fetchNavigation();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Listen for storage changes (login/logout in other tabs or same tab)
  useEffect(() => {
    const handleStorageChange = () => {
      setMember(getInitialMember());
    };
    window.addEventListener('storage', handleStorageChange);
    // Also listen for custom event for same-tab updates
    window.addEventListener('memberUpdate', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('memberUpdate', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (eventsMenuRef.current && !eventsMenuRef.current.contains(event.target)) {
        setIsEventsMenuOpen(false);
      }
      if (infoMenuRef.current && !infoMenuRef.current.contains(event.target)) {
        setIsInfoMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!siteConfig?.logo_url) return;
    const img = new Image();
    img.src = resolveLogo(siteConfig.logo_url);
  }, [siteConfig]);


  const mainLinks = [
    { path: '/', label: 'Inicio' },
  ];

  const eventsLinks = [
    { path: '/catalogo', label: 'Catálogo' },
    { path: '/calendario', label: 'Calendario' },
    { path: '/ciudades', label: 'Ciudades' },
    { path: '/eventos-pasados', label: 'Eventos Pasados' },
    ...customNavItems.filter(i => i.parent === 'eventos').map(i => ({ path: i.url, label: i.label, external: i.open_new_tab })),
  ];

  const infoLinks = [
    { path: '/sobre-nosotros', label: 'Sobre Nosotros' },
    { path: '/condiciones', label: 'Condiciones de Compra' },
    ...customNavItems.filter(i => i.parent === 'informacion').map(i => ({ path: i.url, label: i.label, external: i.open_new_tab })),
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const isEventsActive = eventsLinks.some(link => location.pathname === link.path);
  const isInfoActive = infoLinks.some(link => location.pathname === link.path);
  const isMemberActive = location.pathname.startsWith('/mi-cuenta') || location.pathname.startsWith('/registro') || location.pathname.startsWith('/login');

  return (
    <nav 
      className={`nav-fixed ${isScrolled ? 'nav-solid' : 'nav-transparent'}`}
      data-testid="main-navbar"
    >
      <div className="section-container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img
              src={resolveLogo(siteConfig?.logo_url)}
              alt={siteConfig?.site_name || "Event Cinema"}
              className="h-10 object-contain"
            />
          </Link>
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {mainLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-xs uppercase tracking-widest transition-colors duration-300 ${
                  isActive(link.path) 
                    ? 'text-[var(--primary)]' 
                    : 'text-white/70 hover:text-white'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Events Dropdown */}
            <div className="relative" ref={eventsMenuRef}>
              <button
                onClick={() => setIsEventsMenuOpen(!isEventsMenuOpen)}
                className={`text-xs uppercase tracking-widest transition-colors duration-300 flex items-center gap-1 ${
                  isEventsActive ? 'text-[var(--primary)]' : 'text-white/70 hover:text-white'
                }`}
                data-testid="nav-events-dropdown"
              >
                Eventos
                <ChevronDown size={14} className={`transition-transform ${isEventsMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isEventsMenuOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-[var(--surface)] border border-white/10 shadow-xl z-50">
                  {eventsLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsEventsMenuOpen(false)}
                      className={`block px-4 py-3 text-sm transition-colors ${
                        isActive(link.path) 
                          ? 'text-[var(--primary)] bg-white/5' 
                          : 'text-white/70 hover:text-white hover:bg-white/5'
                      }`}
                      data-testid={`nav-events-${link.label.toLowerCase()}`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            {/* Info Dropdown */}
            <div className="relative" ref={infoMenuRef}>
              <button
                onClick={() => setIsInfoMenuOpen(!isInfoMenuOpen)}
                className={`text-xs uppercase tracking-widest transition-colors duration-300 flex items-center gap-1 ${
                  isInfoActive ? 'text-[var(--primary)]' : 'text-white/70 hover:text-white'
                }`}
                data-testid="nav-info-dropdown"
              >
                Información
                <ChevronDown size={14} className={`transition-transform ${isInfoMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isInfoMenuOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-[var(--surface)] border border-white/10 shadow-xl z-50">
                  {infoLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsInfoMenuOpen(false)}
                      className={`block px-4 py-3 text-sm transition-colors ${
                        isActive(link.path) 
                          ? 'text-[var(--primary)] bg-white/5' 
                          : 'text-white/70 hover:text-white hover:bg-white/5'
                      }`}
                      data-testid={`nav-info-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <NotificationBell />
            
            {/* Member Area / Login */}
            {member ? (
              <Link
                to="/mi-cuenta"
                className={`flex items-center gap-2 text-xs uppercase tracking-widest transition-colors duration-300 ${
                  isMemberActive ? 'text-[var(--primary)]' : 'text-white/70 hover:text-white'
                }`}
                data-testid="nav-member-area"
              >
                <User size={16} />
                <span className="hidden lg:inline">{member.first_name}</span>
              </Link>
            ) : (
              <Link
                to="/mi-cuenta"
                className="btn-primary text-xs"
                data-testid="nav-member-btn"
              >
                myEventCinema
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <NotificationBell />
            <button
              className="text-white p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div 
            className="md:hidden py-6 border-t border-white/10 animate-fadeIn"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-4">
              {mainLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm uppercase tracking-widest py-2 ${
                    isActive(link.path) 
                      ? 'text-[var(--primary)]' 
                      : 'text-white/70'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Mobile Events Section */}
              <div className="pt-2 border-t border-white/10">
                <span className="text-[10px] uppercase tracking-widest text-[var(--primary)] block mb-2">
                  Eventos
                </span>
                {eventsLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`text-sm uppercase tracking-widest py-2 block pl-4 ${
                      isActive(link.path) 
                        ? 'text-[var(--primary)]' 
                        : 'text-white/70'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    data-testid={`mobile-nav-events-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
              
              {/* Mobile Info Section */}
              <div className="pt-2 border-t border-white/10">
                <span className="text-[10px] uppercase tracking-widest text-[var(--primary)] block mb-2">
                  Información
                </span>
                {infoLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`text-sm uppercase tracking-widest py-2 block pl-4 ${
                      isActive(link.path) 
                        ? 'text-[var(--primary)]' 
                        : 'text-white/70'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    data-testid={`mobile-nav-info-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
              
              {/* Mobile Member */}
              <div className="pt-4 border-t border-white/10 flex flex-col gap-3">
                <Link
                  to="/mi-cuenta"
                  className="btn-primary text-center"
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid="mobile-nav-member-btn"
                >
                  {member ? `Hola, ${member.first_name}` : 'myEventCinema'}
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
